import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Início', href: '#inicio' },
  { label: 'Serviços', href: '#servicos' },
  { label: 'Metodologia', href: '#metodologia' },
  { label: 'Sobre', href: '#sobre' },
  { label: 'Contacto', href: '#contacto' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/${href}`);
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#021820]/95 backdrop-blur-md shadow-lg' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="/"
          onClick={handleLogoClick}
          className="flex items-center cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/fb4c53576b1299f0fd427ad804580ce2/9c157f82946044e199e259196c8a09bc.png"
            alt="MoveMind Consulting"
            className={`h-12 w-auto object-contain transition-all duration-500 ${scrolled ? 'brightness-0 invert' : ''}`}
          />
        </a>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={isHome ? link.href : `/${link.href}`}
                onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                className={`hover:text-[#026A86] text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap ${scrolled ? 'text-white/80' : 'text-[#011820]/70'}`}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <a
          href={isHome ? '#contacto' : '/#contacto'}
          onClick={(e) => { e.preventDefault(); handleNav('#contacto'); }}
          className={`hidden md:flex items-center gap-2 border border-[#026A86] text-sm font-medium px-5 py-2 rounded-full hover:bg-[#026A86] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap ${scrolled ? 'text-white' : 'text-[#011820]'}`}
        >
          Fale Connosco
          <i className="ri-arrow-right-line text-xs" />
        </a>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden text-2xl cursor-pointer transition-colors duration-300 ${scrolled ? 'text-white' : 'text-[#011820]'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#021820]/98 backdrop-blur-md px-6 pb-6 pt-2">
          <ul className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={isHome ? link.href : `/${link.href}`}
                  onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                  className="text-white/80 hover:text-[#026A86] text-base font-medium block py-1 cursor-pointer"
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href={isHome ? '#contacto' : '/#contacto'}
                onClick={(e) => { e.preventDefault(); handleNav('#contacto'); }}
                className="inline-flex items-center gap-2 border border-[#026A86] text-white text-sm font-medium px-5 py-2 rounded-full hover:bg-[#026A86] transition-all duration-300 cursor-pointer whitespace-nowrap mt-2"
              >
                Fale Connosco
                <i className="ri-arrow-right-line text-xs" />
              </a>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}
